import { SecurityConfigurationCard } from "features/settings/common/styles";
import Flex from "common/Boxes/Flex";
import styled, { css, useTheme } from "styled-components";
import {
  Platform,
  TelegramCouplingType,
} from "@shared/features/settings/types";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { displayResultMessage, openMessage } from "utils/misc";
import { useNotificationsSettings } from "@shared/features/settings/hooks/useNotificationsSettings";
import {
  MOBILE_NOTIFICATIONS_SETTINGS_COLUMN_SPANS,
  NOTIFICATIONS_SETTINGS_COLUMN_SPANS,
  PLATFORM_TO_DISPLAY_NAME,
  TELEGRAM_NOTIFICATIONS_BOT_LINK,
} from "@shared/features/settings/constants";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import PlatformsSwitch from "features/settings/Security/NotificationsSettings/PlatformsSwitch";
import SettingsSection from "features/settings/Security/NotificationsSettings/SettingsSection";
import Link from "common/Typography/Link";
import useMessage from "utils/hooks/useMessage";
import { useUser } from "@shared/utils/hooks/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import { Globe, Send } from "lucide-react";
import Col from "antd/es/col";
import Row from "antd/es/row";
import Panel from "common/NewLook/Panel.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

const NotificationsSettings = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SETTINGS);
  const { user } = useUser();
  const theme = useTheme();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const [messageApi, contextHolder] = useMessage();

  const openPlatformDisabledMessage = (platform: Platform) => {
    let action: string | React.JSX.Element = t(
      "notifications.register_to_cornix"
    );
    if (platform === TelegramCouplingType.TELEGRAM_BOT) {
      if (user?.is_telegram_connected) {
        action = (
          <>
            {t("notifications.activate_the")}{" "}
            <Link href={TELEGRAM_NOTIFICATIONS_BOT_LINK}>
              {t("notifications.cornix_notifications_bot")}
            </Link>
          </>
        );
      } else {
        action = t("notifications.connect_your_telegram");
      }
    }
    const fullMessage = t("notifications.message");
    const [beforeAction, afterAction] = fullMessage.split("{{action}}");

    const content = (
      <>
        {beforeAction}
        {action}
        {afterAction}
      </>
    );
    messageApi.open({
      content,
      duration: 5,
      type: "warning",
    });
  };

  const {
    notificationsData,
    expanded,
    onSwitchClick,
    onExpandCollapseClick,
    onCheckboxClick,
    loading,
    getNotificationsDataLoading,
    changeNotificationActionLoading,
    getSectionToActionTypes,
  } = useNotificationsSettings(
    displayResultMessage,
    openMessage,
    openPlatformDisabledMessage
  );

  const columnSpans = isMobile
    ? MOBILE_NOTIFICATIONS_SETTINGS_COLUMN_SPANS
    : theme.isNewLook
    ? NEW_LOOK_COLUMN_SPANS
    : NOTIFICATIONS_SETTINGS_COLUMN_SPANS;

  const sections = Object.entries(getSectionToActionTypes()).map(
    ([section, actionTypes], index) => (
      <SettingsSection
        section={section}
        actionTypes={actionTypes}
        key={index}
        index={index}
        expanded={expanded}
        onExpandCollapseClick={onExpandCollapseClick}
        onCheckboxClick={onCheckboxClick}
        notificationsData={notificationsData}
        columnSpans={columnSpans}
      />
    )
  );

  // New look: the channels as toggle rows in one panel, then the
  // notification types as a matrix whose columns line up under the channels.
  if (theme.isNewLook) {
    // First load: the two panels in outline instead of a page spinner.
    if (getNotificationsDataLoading) {
      return (
        <NewLookStack aria-busy={true}>
          <Panel title={t("notifications.channels_title")} flush>
            {[140, 120].map((width) => (
              <SkeletonRow key={width}>
                <Skeleton width={width} height={14} />
                <Skeleton width={40} height={22} radius={11} />
              </SkeletonRow>
            ))}
          </Panel>
          <Panel title={t("notifications.types_title")} flush>
            {SKELETON_TYPE_ROWS.map((width, index) => (
              <SkeletonRow key={index}>
                <Skeleton width={width} height={14} />
                <SkeletonChecks>
                  <Skeleton width={16} height={16} radius={4} />
                  <Skeleton width={16} height={16} radius={4} />
                </SkeletonChecks>
              </SkeletonRow>
            ))}
          </Panel>
        </NewLookStack>
      );
    }
    return (
      <LoadingSpinnerOverlay
        isLoading={loading}
        backgroundColor={theme.colors.layoutBgColor}
        spinnerMarginTop={150}
        hideContentWhileLoading={getNotificationsDataLoading}
        spinnerSize="large"
      >
        <NewLookStack>
          <Panel title={t("notifications.channels_title")} flush>
            <PlatformsSwitch
              notificationsData={notificationsData}
              onSwitchClick={onSwitchClick}
              columnSpans={columnSpans}
            />
          </Panel>
          <Panel title={t("notifications.types_title")} flush>
            <LoadingSpinnerOverlay
              isLoading={changeNotificationActionLoading}
              spinnerMarginTop={100}
            >
              <ColumnsHeader>
                <Col span={columnSpans[0]}>
                  {t("notifications.notification_column")}
                </Col>
                <ColumnHead span={columnSpans[1]}>
                  {isMobile ? (
                    <Send size={14} strokeWidth={1.75} aria-label="Telegram" />
                  ) : (
                    PLATFORM_TO_DISPLAY_NAME[TelegramCouplingType.TELEGRAM_BOT]
                  )}
                </ColumnHead>
                <ColumnHead span={columnSpans[2]}>
                  {isMobile ? (
                    <Globe
                      size={14}
                      strokeWidth={1.75}
                      aria-label={
                        PLATFORM_TO_DISPLAY_NAME[
                          TelegramCouplingType.WEB_OR_APP
                        ]
                      }
                    />
                  ) : (
                    PLATFORM_TO_DISPLAY_NAME[TelegramCouplingType.WEB_OR_APP]
                  )}
                </ColumnHead>
              </ColumnsHeader>
              {sections}
            </LoadingSpinnerOverlay>
          </Panel>
        </NewLookStack>
        {contextHolder}
      </LoadingSpinnerOverlay>
    );
  }

  return (
    <LoadingSpinnerOverlay
      isLoading={loading}
      backgroundColor={theme.colors.layoutBgColor}
      spinnerMarginTop={150}
      hideContentWhileLoading={getNotificationsDataLoading}
      spinnerSize="large"
    >
      <Wrapper $isMobile={isMobile}>
        <SecurityConfigurationCard>
          <LoadingSpinnerOverlay
            isLoading={changeNotificationActionLoading}
            spinnerMarginTop={100}
          >
            <PlatformsSwitch
              notificationsData={notificationsData}
              onSwitchClick={onSwitchClick}
              columnSpans={columnSpans}
            />
            {sections}
          </LoadingSpinnerOverlay>
        </SecurityConfigurationCard>
      </Wrapper>
      {contextHolder}
    </LoadingSpinnerOverlay>
  );
};

export default NotificationsSettings;

const SKELETON_TYPE_ROWS = [96, 140, 120, 180, 110, 150, 130, 160];

// Equal channel columns so the checkboxes line up under their headers.
const NEW_LOOK_COLUMN_SPANS = [12, 6, 6];

const Wrapper = styled(Flex)<{ $isMobile?: boolean }>`
  padding: ${(p) => (p.$isMobile ? 12 : 24)}px;
  width: 100%;
  justify-content: center;
  flex-direction: column;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      padding: 0;
    `}
`;
const NewLookStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const ColumnsHeader = styled(Row)`
  align-items: center;
  min-height: 40px;
  padding: 0 20px;
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  ${newLookType.micro};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const ColumnHead = styled(Col)`
  display: flex;
  justify-content: center;
  text-align: center;
`;
const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 48px;
  padding: 0 20px;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
/** Two boxes under the channel columns (each a quarter of the row). */
const SkeletonChecks = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  justify-items: center;
  width: 50%;
`;
